import { logEvent } from "firebase/analytics";
import firebaseConfig from "../../../config/firebaseConfig";
import { shouldCollectAnalytics } from "../env/analyticsEnv";
import { applyFirebaseAnalyticsDebugGlobal } from "../../../services/analytics/applyFirebaseAnalyticsDebugGlobal";
import { acquireFirebaseAnalytics } from "../../../services/analytics/firebaseAnalyticsInstance";

/** @type {import('firebase/analytics').Analytics | null} */
let analyticsInstance = null;
let initAttempted = false;

function resolveMeasurementId() {
  return (
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ||
    firebaseConfig.measurementId ||
    ""
  );
}

/** @type {typeof logEvent | null} */
let logEventFn = null;

/**
 * Injectable client for tests.
 * @typedef {{ logEvent: (analytics: unknown, name: string, params?: object) => void } | null} AnalyticsClient
 */

/** @type {AnalyticsClient} */
let clientOverride = null;

export function setAnalyticsClientForTests(client) {
  clientOverride = client;
}

export function resetAnalyticsClientForTests() {
  clientOverride = null;
  analyticsInstance = null;
  initAttempted = false;
  logEventFn = null;
}

/**
 * Initialize Firebase Analytics (production or explicit debug/force only).
 * @returns {Promise<boolean>} whether analytics is ready
 */
export async function initFirebaseAnalytics() {
  if (initAttempted) return Boolean(analyticsInstance || clientOverride);
  initAttempted = true;

  if (!shouldCollectAnalytics()) return false;
  if (!resolveMeasurementId()) return false;
  if (typeof window === "undefined") return false;

  applyFirebaseAnalyticsDebugGlobal();

  if (clientOverride) {
    logEventFn = clientOverride.logEvent;
    return true;
  }

  try {
    analyticsInstance = await acquireFirebaseAnalytics();
    if (!analyticsInstance) return false;
    logEventFn = logEvent;
    return true;
  } catch {
    analyticsInstance = null;
    return false;
  }
}

export function isAnalyticsInitialized() {
  return Boolean((analyticsInstance || clientOverride) && logEventFn);
}

/**
 * Low-level event sink (async, non-blocking).
 * @param {string} name
 * @param {Record<string, string | number | boolean>} params
 */
export function sendAnalyticsEvent(name, params) {
  if (!shouldCollectAnalytics() || !logEventFn) return;
  const target = analyticsInstance || {};
  queueMicrotask(() => {
    try {
      logEventFn(target, name, params);
    } catch {
      /* graceful failure */
    }
  });
}
